import { useState, useEffect, useRef } from 'react';
import PaymentSummaryModal from '../../../components/PaymentSummaryModal';

interface SessionFields {
  previousCash: number;
  openingCash: number;
  salesAmount: number;
  cashIn: number;
  cashOut: number;
  receivedCash: number;
  receivedBankAmount: number;
  salesReturnAmount: number;
  salesRefundAmount: number;
  adjustedCreditAmount: number;
}

interface CashReconciliationModalProps {
  onClose: () => void;
  onConfirm: (countedAmount: number, varianceReason: string) => void;
  sessionFields: SessionFields;
  sessionId?: number;
  isLoading?: boolean;
}

const BILL_DENOMINATIONS = [
  { key: 'thousand', value: 1000 },
  { key: 'fiveHundred', value: 500 },
  { key: 'twoHundred', value: 200 },
  { key: 'hundred', value: 100 },
  { key: 'fifty', value: 50 },
  { key: 'twenty', value: 20 },
  { key: 'ten', value: 10 },
  { key: 'five', value: 5 },
  { key: 'two', value: 2 },
  { key: 'one', value: 1 },
] as const;

type BillKey = typeof BILL_DENOMINATIONS[number]['key'];
type BillCounts = Record<BillKey, number>;

const EMPTY_BILL_COUNTS: BillCounts = {
  thousand: 0, fiveHundred: 0, twoHundred: 0, hundred: 0, fifty: 0, twenty: 0, ten: 0, five: 0, two: 0, one: 0,
};

export default function CashReconciliationModal({
  onClose,
  onConfirm,
  sessionFields,
  sessionId,
  isLoading = false,
}: CashReconciliationModalProps) {
  const [countedAmount, setCountedAmount] = useState('');
  const [showPaymentSummary, setShowPaymentSummary] = useState(false);
  const [varianceReason, setVarianceReason] = useState('');
  const [acceptDifference, setAcceptDifference] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState<'count' | 'variance'>('count');
  const [showDenominations, setShowDenominations] = useState(false);
  const [billCounts, setBillCounts] = useState<BillCounts>(EMPTY_BILL_COUNTS);
  const {
    previousCash,
    openingCash,
    salesAmount,
    cashIn,
    cashOut,
    receivedCash,
    receivedBankAmount,
    salesReturnAmount,
    salesRefundAmount,
    adjustedCreditAmount,
  } = sessionFields;

  const closingCash =
    previousCash + openingCash + salesAmount + cashIn - cashOut - receivedBankAmount - salesReturnAmount;

  const variance = countedAmount ? parseFloat(countedAmount) - closingCash : 0;
  const hasVariance = Math.abs(variance) > 0.01;
  const isLargeVariance = Math.abs(variance) > 50;

  const calculateBillTotal = () =>
    BILL_DENOMINATIONS.reduce((sum, bill) => sum + billCounts[bill.key] * bill.value, 0);

  useEffect(() => {
    if (showDenominations) {
      const total = calculateBillTotal();
      setCountedAmount(total > 0 ? total.toFixed(2) : '');
      setAcceptDifference(false);
      setError('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDenominations, billCounts]);

  const handleBillCountChange = (key: BillKey, value: string) => {
    const digitsOnly = value.replace(/\D/g, '');
    const numValue = digitsOnly === '' ? 0 : parseInt(digitsOnly, 10);
    setBillCounts({ ...billCounts, [key]: numValue });
  };

  const handleToggleDenominations = () => {
    const next = !showDenominations;
    setShowDenominations(next);
    if (!next) {
      setBillCounts(EMPTY_BILL_COUNTS);
    }
  };

  const handleContinueRef = useRef<() => void>(() => {});
  const handleConfirmRef = useRef<() => void>(() => {});
  useEffect(() => { handleContinueRef.current = handleContinue; handleConfirmRef.current = handleConfirm; });
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        if (step === 'count') handleContinueRef.current();
        else handleConfirmRef.current();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [step]);

  const handleContinue = () => {
    const numAmount = parseFloat(countedAmount);
    if (!countedAmount || numAmount < 0) {
      setError('Please enter a valid counted amount');
      return;
    }
    onConfirm(numAmount, '');
  };

  const handleConfirm = () => {
    if (hasVariance && !varianceReason.trim()) {
      setError('Please provide a reason for the variance');
      return;
    }
    if (isLargeVariance && !varianceReason.trim()) {
      setError('Large variance requires detailed explanation');
      return;
    }
    onConfirm(parseFloat(countedAmount), varianceReason);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className={`bg-white rounded-lg shadow-xl w-full ${showDenominations ? 'max-w-4xl' : 'max-w-2xl'} max-h-[90vh] min-h-[300px] overflow-y-auto transition-[max-width] duration-200`}>
        <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-white">
          <div className="flex items-center gap-2">
            <i className="ri-calculator-line text-xl text-gray-700"></i>
            <h2 className="text-lg font-semibold text-gray-900">Cash Reconciliation - Shift Close</h2>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-full transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl text-gray-600"></i>
          </button>
        </div>

        <div className="p-4 space-y-3">
          {step === 'count' && (
            <div className="grid grid-cols-2 gap-3">
              {/* Left: Session Summary */}
              <div>
                <div className="flex items-center gap-2 mb-1.5">
                  <i className="ri-information-line text-blue-600"></i>
                  <p className="text-sm font-medium text-blue-900">Session Summary</p>
                </div>
                {isLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <i className="ri-loader-4-line animate-spin text-2xl text-teal-600 mr-2"></i>
                    <span className="text-sm text-gray-500">Loading session data...</span>
                  </div>
                ) : (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-2.5 space-y-1">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Previous Cash:</span>
                      <span className="text-sm font-medium text-gray-900">{previousCash.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Opening Cash:</span>
                      <span className="text-sm font-medium text-gray-900">{openingCash.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-green-600">+ Sales:</span>
                      <span className="text-sm font-medium text-green-600">{salesAmount.toFixed(2)}</span>
                    </div>
                    {/* Cash In / Cash Out line hidden for now — may be revisited in the future.
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-green-600">+ Cash In:</span>
                      <span className="text-sm font-medium text-green-600">{cashIn.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-red-600">- Cash Out:</span>
                      <span className="text-sm font-medium text-red-600">{cashOut.toFixed(2)}</span>
                    </div>
                    */}
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Cash Amount:</span>
                      <span className="text-sm font-medium text-gray-900">{receivedCash.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <button
                        type="button"
                        onClick={() => setShowPaymentSummary(true)}
                        disabled={!sessionId}
                        className="flex items-center gap-1 text-sm text-red-600 hover:text-red-700 disabled:cursor-not-allowed disabled:opacity-70 cursor-pointer"
                      >
                        <span>- Bank/Mobile:</span>
                        <i className="ri-eye-line text-xs"></i>
                      </button>
                      <span className="text-sm font-medium text-red-600">{receivedBankAmount.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-red-600">- Sales Return:</span>
                      <span className="text-sm font-medium text-red-600">{salesReturnAmount.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-red-600">- Refund:</span>
                      <span className="text-sm font-medium text-red-600">{salesRefundAmount.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Credit Applied:</span>
                      <span className="text-sm font-medium text-gray-900">{adjustedCreditAmount.toFixed(2)}</span>
                    </div>
                    <div className="pt-2 border-t border-gray-300 flex justify-between items-center">
                      <span className="text-sm font-semibold text-gray-900">Closing Cash:</span>
                      <span className="text-lg font-bold text-gray-900">{closingCash.toFixed(2)}</span>
                    </div>
                  </div>
                )}

                {showDenominations && countedAmount && parseFloat(countedAmount) >= 0 && (
                  <div className={`mt-2 rounded-lg p-2.5 ${hasVariance ? 'bg-yellow-50 border border-yellow-200' : 'bg-green-50 border border-green-200'}`}>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700">Difference:</span>
                      <span className={`text-lg font-bold ${variance > 0 ? 'text-green-600' : variance < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                        {variance > 0 ? '+' : ''}{variance.toFixed(2)}
                      </span>
                    </div>
                    {hasVariance && (
                      <p className="text-xs text-gray-600 mt-1">
                        {variance > 0 ? 'Cash over expected amount' : 'Cash short of expected amount'}
                      </p>
                    )}
                  </div>
                )}

                {showDenominations && hasVariance && countedAmount && (
                  <label className="mt-2 flex items-center gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={acceptDifference}
                      onChange={(e) => {
                        setAcceptDifference(e.target.checked);
                        setError('');
                      }}
                      className="w-4 h-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                    />
                    <span className="text-sm text-gray-700">Accept Payment Difference.</span>
                  </label>
                )}
              </div>

              {/* Right: Count + Difference + Accept */}
              <div className="flex flex-col gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Count Physical Cash <span className="text-red-500">*</span>
                  </label>

                  <button
                    type="button"
                    onClick={handleToggleDenominations}
                    className="flex items-center gap-2 text-sm text-teal-600 hover:text-teal-700 font-medium cursor-pointer mb-3"
                  >
                    <i className={`ri-${showDenominations ? 'checkbox' : 'checkbox-blank'}-line text-lg`}></i>
                    Use cash denomination breakdown
                  </button>

                  {showDenominations && (
                    <div className="grid grid-cols-3 gap-2 mb-3">
                      {BILL_DENOMINATIONS.map((bill) => (
                        <div key={bill.key} className="border border-gray-200 rounded-lg p-2">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-xs font-semibold text-gray-700">৳{bill.value.toLocaleString()}</span>
                            <input
                              type="text"
                              inputMode="numeric"
                              pattern="[0-9]*"
                              placeholder="0"
                              value={billCounts[bill.key] || ''}
                              onChange={(e) => handleBillCountChange(bill.key, e.target.value)}
                              className="w-14 px-1.5 py-1 text-xs text-center border border-gray-300 rounded focus:border-teal-500 focus:outline-none"
                            />
                          </div>
                          <p className="mt-1 text-[11px] text-gray-500 text-right">= ৳{(billCounts[bill.key] * bill.value).toLocaleString()}</p>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xl">৳</span>
                    <input
                      type={showDenominations ? 'text' : 'number'} onWheel={(e) => e.currentTarget.blur()}
                      value={countedAmount}
                      readOnly={showDenominations}
                      disabled={showDenominations}
                      onChange={(e) => {
                        if (showDenominations) return;
                        setCountedAmount(e.target.value);
                        setAcceptDifference(false);
                        setError('');
                      }}
                      placeholder="0.00"
                      step="0.01"
                      min="0"
                      className={`w-full pl-10 pr-3 py-3 border-2 rounded-lg text-2xl font-bold ${showDenominations ? 'bg-gray-50 border-gray-200 text-gray-900' : 'border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent'}`}
                      autoFocus={!showDenominations}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    {showDenominations ? 'Auto-calculated from denomination counts above' : 'Count all bills and coins in the drawer'}
                  </p>
                </div>

                {!showDenominations && countedAmount && parseFloat(countedAmount) >= 0 && (
                  <div className={`rounded-lg p-2.5 ${hasVariance ? 'bg-yellow-50 border border-yellow-200' : 'bg-green-50 border border-green-200'}`}>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700">Difference:</span>
                      <span className={`text-lg font-bold ${variance > 0 ? 'text-green-600' : variance < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                        {variance > 0 ? '+' : ''}{variance.toFixed(2)}
                      </span>
                    </div>
                    {hasVariance && (
                      <p className="text-xs text-gray-600 mt-1">
                        {variance > 0 ? 'Cash over expected amount' : 'Cash short of expected amount'}
                      </p>
                    )}
                  </div>
                )}

                {!showDenominations && hasVariance && countedAmount && (
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={acceptDifference}
                      onChange={(e) => {
                        setAcceptDifference(e.target.checked);
                        setError('');
                      }}
                      className="w-4 h-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                    />
                    <span className="text-sm text-gray-700">Accept Payment Difference.</span>
                  </label>
                )}

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
                    <i className="ri-error-warning-line text-red-500"></i>
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {step === 'variance' && (
            <>
              <div className={`${isLargeVariance ? 'bg-red-50 border-red-200' : 'bg-yellow-50 border-yellow-200'} border rounded-lg p-4`}>
                <div className="flex items-start gap-3">
                  <i className={`${isLargeVariance ? 'ri-error-warning-fill text-red-600' : 'ri-alert-line text-yellow-600'} text-xl mt-0.5`}></i>
                  <div>
                    <p className={`text-sm font-medium ${isLargeVariance ? 'text-red-900' : 'text-yellow-900'}`}>
                      {isLargeVariance ? 'Large Variance Detected' : 'Variance Detected'}
                    </p>
                    <p className={`text-xs ${isLargeVariance ? 'text-red-700' : 'text-yellow-700'} mt-1`}>
                      {isLargeVariance
                        ? 'This variance exceeds 50 and requires supervisor approval and detailed explanation'
                        : 'Please provide a reason for the cash variance'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-xs text-gray-600 mb-1">Closing Cash</p>
                    <p className="text-lg font-semibold text-gray-900">{closingCash.toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 mb-1">Counted</p>
                    <p className="text-lg font-semibold text-gray-900">{parseFloat(countedAmount).toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 mb-1">Difference</p>
                    <p className={`text-lg font-bold ${variance > 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {variance > 0 ? '+' : ''}{variance.toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Variance Reason <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={varianceReason}
                  onChange={(e) => {
                    setVarianceReason(e.target.value);
                    setError('');
                  }}
                  placeholder="Explain the reason for the variance (e.g., counting error, missing transaction, incorrect change given...)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none text-sm"
                  rows={4}
                  maxLength={500}
                  autoFocus
                />
                <p className="text-xs text-gray-500 mt-1">{varianceReason.length}/500 characters</p>
              </div>

              {isLargeVariance && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                  <i className="ri-shield-user-line text-red-500 text-lg mt-0.5"></i>
                  <div>
                    <p className="text-sm font-medium text-red-800">Supervisor Approval Required</p>
                    <p className="text-xs text-red-600 mt-1">A supervisor must review and approve this reconciliation before shift close</p>
                  </div>
                </div>
              )}

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
                  <i className="ri-error-warning-line text-red-500"></i>
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex gap-3 p-4 border-t bg-gray-50 sticky bottom-0">
          {step === 'variance' && (
            <button
              onClick={() => setStep('count')}
              className="px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-100 font-medium text-gray-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-arrow-left-line mr-2"></i>
              Back
            </button>
          )}
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-100 font-medium text-gray-700 transition-colors whitespace-nowrap cursor-pointer"
          >
            Cancel
          </button>
          <button
            onClick={step === 'count' ? handleContinue : handleConfirm}
            disabled={step === 'count' && hasVariance && !acceptDifference}
            className="flex-1 px-4 py-2.5 bg-teal-600 hover:bg-teal-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer"
          >
            {step === 'count' ? 'Continue' : 'Complete Reconciliation'}
          </button>
        </div>
      </div>

      {showPaymentSummary && sessionId && (
        <PaymentSummaryModal
          sessionId={sessionId}
          type="counter"
          onClose={() => setShowPaymentSummary(false)}
        />
      )}
    </div>
  );
}
